import { useState } from "react";
import { useSite } from "../../lib/useSite";
import { api, apiError } from "../../lib/api";
import { Eyebrow, Reveal } from "../../components/bits";
import { toast } from "sonner";
import { Building2, ClipboardList, ShieldCheck, CalendarClock, Upload, CheckCircle2 } from "lucide-react";

const COM = "https://images.pexels.com/photos/22711217/pexels-photo-22711217.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const field = "w-full bg-white border border-[#e0ddd4] rounded-sm px-4 py-3 text-sm focus:border-[#d4af37]";
const labelCls = "text-xs tracking-wide text-[#555] uppercase mb-1.5 block";

export default function Commercial() {
  const site = useSite();
  const insuranceActive = site?.insurance?.active;
  const [f, setF] = useState({
    company_name: "", contact_name: "", email: "", phone: "", site_address: "", premises_type: "",
    num_sites: "", floor_area: "", frequency: "", preferred_schedule: "", estimated_hours: "",
    washrooms: "", kitchens: "", communal_areas: "", specialist_requirements: "", existing_provider: "",
    start_date: "", notes: "", spec_file: "",
  });
  const [done, setDone] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader(); r.onload = () => set("spec_file", r.result); r.readAsDataURL(file);
    toast.success("File attached");
  };

  const submit = async () => {
    if (!f.company_name || !f.contact_name || !/.+@.+\..+/.test(f.email)) { toast.error("Please complete company, contact and a valid email"); return; }
    try {
      const { data } = await api.post("/public/commercial-enquiry", { ...f, num_sites: f.num_sites ? Number(f.num_sites) : null });
      setDone(data.reference); toast.success("Commercial enquiry received");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (done) return (
    <div className="max-w-xl mx-auto px-5 py-24 text-center">
      <CheckCircle2 className="text-[#d4af37] mx-auto" size={50} />
      <h1 className="font-display text-4xl mt-6">Enquiry received</h1>
      <p className="text-[#555] mt-3">Reference {done}. Your commercial requirements will be individually assessed and quoted to ensure the correct service, staffing and compliance arrangements are in place. Our team will be in touch.</p>
    </div>
  );

  return (
    <div>
      <section className="relative h-[52vh] flex items-center">
        <img src={COM} alt="Office" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0a0a0a]/72" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <Eyebrow dark>Commercial</Eyebrow>
          <h1 className="font-display text-[#fdfbf7] text-5xl sm:text-6xl tracking-tight max-w-2xl">Commercial Cleaning</h1>
          <p className="text-[#cfcabf] mt-5 max-w-2xl leading-relaxed">
            Black Diamond Elite Cleaning welcomes enquiries from offices, landlords, letting agents, property managers and
            other organisations. Commercial requirements are individually assessed and quoted to ensure the correct service,
            staffing and compliance arrangements are in place.
          </p>
          <a href="#commercial-quote" className="btn-gold inline-block mt-7 px-8 py-4 rounded-sm" data-testid="commercial-cta">Request a Commercial Quote</a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-5">
        {[[Building2, "Multiple sites", "Coordinated cleaning across all your locations with site contacts."],
          [ClipboardList, "Site-specific checklists", "Tailored scopes, schedules and SLA notes per site."],
          [CalendarClock, "Recurring shifts", "Reliable daily, weekly or bespoke schedules and staff assignments."],
          [ShieldCheck, "Individually assessed", "Every commercial requirement is reviewed before quotation."]].map(([Icon, t, b], i) => (
          <Reveal key={t} delay={i * 0.05}>
            <div className="card-luxe p-7 h-full"><Icon className="text-[#d4af37]" size={24} /><h3 className="font-display text-xl mt-4">{t}</h3><p className="text-sm text-[#555] mt-2">{b}</p></div>
          </Reveal>
        ))}
      </section>

      <section id="commercial-quote" className="max-w-3xl mx-auto px-5 sm:px-8 pb-20">
        <div className="card-luxe p-8">
          <h2 className="font-display text-3xl tracking-tight">Request a Commercial Quote</h2>
          <p className="text-sm text-[#555] mt-2">Commercial work is arranged directly by our team following assessment — it cannot be instantly booked online.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
            <div><label className={labelCls}>Company name *</label><input data-testid="ce-company" className={field} value={f.company_name} onChange={(e) => set("company_name", e.target.value)} /></div>
            <div><label className={labelCls}>Contact name *</label><input data-testid="ce-contact" className={field} value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} /></div>
            <div><label className={labelCls}>Email *</label><input data-testid="ce-email" className={field} value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div><label className={labelCls}>Telephone</label><input className={field} value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Site address</label><input className={field} value={f.site_address} onChange={(e) => set("site_address", e.target.value)} /></div>
            <div><label className={labelCls}>Type of premises</label><input className={field} value={f.premises_type} onChange={(e) => set("premises_type", e.target.value)} placeholder="Office, retail, communal…" /></div>
            <div><label className={labelCls}>Number of sites</label><input type="number" className={field} value={f.num_sites} onChange={(e) => set("num_sites", e.target.value)} /></div>
            <div><label className={labelCls}>Approx. floor area</label><input className={field} value={f.floor_area} onChange={(e) => set("floor_area", e.target.value)} /></div>
            <div><label className={labelCls}>Required frequency</label><input className={field} value={f.frequency} onChange={(e) => set("frequency", e.target.value)} placeholder="Daily, weekly…" /></div>
            <div><label className={labelCls}>Preferred schedule</label><input className={field} value={f.preferred_schedule} onChange={(e) => set("preferred_schedule", e.target.value)} placeholder="Evenings, weekends…" /></div>
            <div><label className={labelCls}>Estimated hours (if known)</label><input className={field} value={f.estimated_hours} onChange={(e) => set("estimated_hours", e.target.value)} /></div>
            <div><label className={labelCls}>Washrooms</label><input className={field} value={f.washrooms} onChange={(e) => set("washrooms", e.target.value)} /></div>
            <div><label className={labelCls}>Kitchens</label><input className={field} value={f.kitchens} onChange={(e) => set("kitchens", e.target.value)} /></div>
            <div><label className={labelCls}>Communal areas</label><input className={field} value={f.communal_areas} onChange={(e) => set("communal_areas", e.target.value)} /></div>
            <div><label className={labelCls}>Existing cleaning provider</label><input className={field} value={f.existing_provider} onChange={(e) => set("existing_provider", e.target.value)} /></div>
            <div><label className={labelCls}>Desired start date</label><input type="date" className={field} value={f.start_date} onChange={(e) => set("start_date", e.target.value)} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Specialist requirements</label><textarea rows="2" className={field} value={f.specialist_requirements} onChange={(e) => set("specialist_requirements", e.target.value)} /></div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Upload tender / specification</label>
              <label className="flex items-center gap-2 border border-dashed border-[#cfcabf] rounded-sm px-4 py-3 cursor-pointer text-sm text-[#555]">
                <Upload size={16} /> {f.spec_file ? "File attached" : "Choose file"}
                <input type="file" className="hidden" onChange={onFile} data-testid="ce-file" />
              </label>
            </div>
            <div className="sm:col-span-2"><label className={labelCls}>Notes</label><textarea rows="3" className={field} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></div>
            <div className="sm:col-span-2"><button data-testid="ce-submit" onClick={submit} className="btn-gold px-8 py-3 rounded-sm">Request a Commercial Quote</button></div>
          </div>
        </div>
        {insuranceActive && (
          <div className="mt-8 border border-[#e0ddd4] rounded-sm p-6 bg-white">
            <p className="text-[11px] tracking-luxe uppercase text-[#b5952f] mb-3">Insurance</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {site.insurance.claims.map((c) => (
                <p key={c} className="flex items-center gap-2 text-sm"><CheckCircle2 size={15} className="text-[#d4af37]" /> {c}</p>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
